import { useCallback, useState } from 'react';

import { Select } from '@/lib/base/forms/select/select';
import { ConfirmationDialog } from '@/lib/base/overlays/confirmation-dialog';
import { getPortfolioActionError } from '@/lib/portfolio/errors';
import { useSetMemberRole } from '@/lib/portfolio/hooks/useSetMemberRole';
import type { PortfolioRole } from '@/lib/portfolio/types';

const ROLE_ITEMS = [
  { id: 'Admin', label: 'Admin' },
  { id: 'User', label: 'User' },
];

interface MemberRoleControlProps {
  portfolioId: number;
  orgId: number;
  userId: number;
  displayName: string;
  isAdmin: boolean;
}

export function MemberRoleControl({
  portfolioId,
  orgId,
  userId,
  displayName,
  isAdmin,
}: MemberRoleControlProps) {
  const roleMutation = useSetMemberRole(portfolioId, orgId);
  // Role selected in the dropdown but not yet confirmed. A role change is a
  // consequential admin action (it can demote an org's administrator), so it
  // always passes through a confirmation dialog before mutating.
  const [pendingRole, setPendingRole] = useState<PortfolioRole | null>(null);

  const handleRoleChange = useCallback(
    (key: string | number | null) => {
      if (key == null) {
        return;
      }
      const roleName = String(key) as PortfolioRole;
      if ((roleName === 'Admin') === isAdmin) {
        return;
      }
      setPendingRole(roleName);
    },
    [isAdmin]
  );

  const handleConfirm = useCallback(() => {
    if (pendingRole != null) {
      roleMutation.mutate({ userId, roleName: pendingRole });
    }
    setPendingRole(null);
  }, [pendingRole, roleMutation, userId]);

  const handleCancel = useCallback(() => {
    setPendingRole(null);
  }, []);

  const isDemotion = pendingRole === 'User';

  return (
    <div>
      <Select
        size='sm'
        items={ROLE_ITEMS}
        selectedKey={isAdmin ? 'Admin' : 'User'}
        onSelectionChange={handleRoleChange}
        isDisabled={roleMutation.isPending}
        aria-label={`Role for ${displayName}`}
      >
        {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
      </Select>
      {roleMutation.isError && (
        <p
          role='alert'
          className='mt-1 text-body-small text-[var(--color-text-error-primary)]'
        >
          {getPortfolioActionError(roleMutation.error)}
        </p>
      )}
      <ConfirmationDialog
        isOpen={pendingRole != null}
        title={`Change ${displayName}'s role?`}
        description={
          isDemotion
            ? `${displayName} will lose admin access to this organization.`
            : `${displayName} will get admin access to this organization.`
        }
        confirmLabel='Change role'
        variant={isDemotion ? 'warning' : 'default'}
        instance='change-member-role'
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}
